import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { ADD_ROUTINE_ITEM, MY_WEEKLY_ROUTINE } from 'api/graphql/play';
import { createSnack } from 'common/utils/general';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import uiSlice from 'store/modules/ui';
import { DAY_VALUES, dayShort } from 'components/play/constants';

/**
 * OTRIX Play — pick a weekday (and optional sets/reps) to add an exercise to the
 * client's weekly routine.
 */
function AddToRoutineDialog({ open, exercise = null, onClose }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const [day, setDay] = useState(1);
  const [sets, setSets] = useState('');
  const [reps, setReps] = useState('');

  // Sync the suggested sets/reps each time the dialog opens with a different exercise.
  useEffect(() => {
    setSets(exercise?.suggested_sets || '');
    setReps(exercise?.suggested_reps || '');
    setDay(1);
  }, [exercise]);

  const [addItem, { loading }] = useMutation(ADD_ROUTINE_ITEM, {
    refetchQueries: [{ query: MY_WEEKLY_ROUTINE }]
  });

  const handleAdd = async () => {
    try {
      await addItem({
        variables: {
          exercise_id: exercise.id,
          day_of_week: day,
          target_sets: sets ? parseInt(sets, 10) || null : null,
          target_reps: reps || null
        }
      });
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({ key: `add-${Date.now()}`, variant: 'success', message: t('addDialog.success', { ns: 'play' }) })
        )
      );
      onClose();
    } catch {
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({ key: `add-err-${Date.now()}`, variant: 'error', message: t('addDialog.error', { ns: 'play' }) })
        )
      );
    }
  };

  if (!exercise) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>{t('addDialog.title', { ns: 'play', name: exercise.localized_name })}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('addDialog.subtitle', { ns: 'play', defaultValue: 'Elige el día de tu semana y ajusta series y repeticiones.' })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ mb: 1 }}>
          {t('addDialog.chooseDay', { ns: 'play' })}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75, mb: 2.5 }}>
          {DAY_VALUES.map((d) => {
            const active = day === d;
            return (
              <Box
                key={d}
                role="button"
                tabIndex={0}
                onClick={() => setDay(d)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDay(d); } }}
                sx={{
                  py: 1,
                  textAlign: 'center',
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: active ? 'hairline.strong' : 'hairline.main',
                  bgcolor: active ? 'selected.bg' : 'surface.raised',
                  color: active ? 'selected.fg' : 'text.secondary',
                  fontWeight: active ? 800 : 600,
                  fontSize: '0.72rem',
                  transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s, background-color 0.2s',
                  '&:hover': { borderColor: 'hairline.strong' },
                  '&:active': { transform: 'scale(0.96)' }
                }}>
                {dayShort(t, d)}
              </Box>
            );
          })}
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            label={t('addDialog.series', { ns: 'play' })}
            size="small"
            value={sets}
            onChange={(e) => setSets(e.target.value)}
            inputProps={{ inputMode: 'numeric' }}
            fullWidth
          />
          <TextField
            label={t('addDialog.reps', { ns: 'play' })}
            size="small"
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            placeholder="8-12"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}>
          {t('addDialog.cancel', { ns: 'play' })}
        </Button>
        <Button onClick={handleAdd} disabled={loading} sx={{ ...premiumButtonSx, px: 3, py: 1 }}>
          {t('addDialog.add', { ns: 'play' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddToRoutineDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  exercise: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default AddToRoutineDialog;
